(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const content = drawer.querySelector('[data-cart-drawer-content]');
  const panel = drawer.querySelector('.cart-drawer-panel');
  const upsellProductsNode = document.querySelector('[data-cart-upsell-products]');
  const root = window.Shopify?.routes?.root || '/';
  const freeShippingThreshold = 7500;
  let returnFocus = null;

  const readUpsellProducts = () => {
    try {
      return JSON.parse(upsellProductsNode?.textContent || '{}');
    } catch (error) {
      return {};
    }
  };

  const upsellProducts = readUpsellProducts();

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD'
  }).format(cents / 100);

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const itemMeta = (item) => {
    const parts = [];
    if (item.variant_title && item.variant_title !== 'Default Title') parts.push(item.variant_title);
    if (item.selling_plan_allocation?.selling_plan?.name) parts.push(item.selling_plan_allocation.selling_plan.name);
    return parts.map(escapeHtml).join(' · ');
  };

  const resizedImage = (url) => `${url}${url.includes('?') ? '&' : '?'}width=180`;

  const bundleIdFor = (item) => item.properties?._bundleId || item.properties?.bundleId;

  const replaceTemplate = (template, values) => Object.entries(values).reduce(
    (result, [key, value]) => result.replace(key, String(value)),
    template || ''
  );

  const renderUpsell = (cart, entries) => {
    if (cart.item_count !== 1 || entries.length !== 1 || entries[0].type !== 'item') return '';

    const { item, index } = entries[0];
    if (item.quantity !== 1 || bundleIdFor(item)) return '';

    const offer = upsellProducts[item.handle];
    if (!offer?.bundleUrl || !offer?.bundleSavings) return '';

    const subscription = Boolean(item.selling_plan_allocation);
    const twoPackPrice = subscription ? offer.twoPackSubscriptionPrice : offer.twoPackPrice;
    if (twoPackPrice == null) return '';
    const twoPackSavings = Math.max(0, offer.twoPackRegular - twoPackPrice);
    const twoPackAction = subscription
      ? `<a class="cart-upsell-cta" href="${escapeHtml(offer.productUrl)}">${escapeHtml(replaceTemplate(drawer.dataset.upsellViewTwoTemplate, { '__AMOUNT__': money(twoPackSavings) }))}</a>`
      : `<button class="cart-upsell-cta" type="button" data-cart-change="${index + 1}" data-cart-quantity="2">${escapeHtml(replaceTemplate(drawer.dataset.upsellSwitchTemplate, { '__AMOUNT__': money(twoPackSavings) }))}</button>`;

    return `<aside class="cart-upsell" aria-labelledby="CartUpsellTitle">
      <div class="cart-upsell-header">
        <span class="cart-upsell-kicker">${escapeHtml(drawer.dataset.upsellKicker)}</span>
        <h3 id="CartUpsellTitle">${escapeHtml(drawer.dataset.upsellTitle)}</h3>
        <p>${escapeHtml(drawer.dataset.upsellBody)}</p>
      </div>
      <div class="cart-upsell-options">
        <article class="cart-upsell-option">
          <span class="cart-upsell-option-label">${escapeHtml(drawer.dataset.upsellTwoPackLabel)}</span>
          <div class="cart-upsell-option-heading">
            <strong>${escapeHtml(drawer.dataset.upsellTwoPackTitle)}</strong>
            <span class="cart-upsell-save">${escapeHtml(replaceTemplate(drawer.dataset.upsellSaveTemplate, { '__AMOUNT__': money(twoPackSavings) }))}</span>
          </div>
          <p>${escapeHtml(drawer.dataset.upsellTwoPackBody)}</p>
          <div class="cart-upsell-price"><strong>${money(twoPackPrice)}</strong><s>${money(offer.twoPackRegular)}</s></div>
          ${twoPackAction}
        </article>
        <article class="cart-upsell-option cart-upsell-option--bundle">
          <span class="cart-upsell-option-label">${escapeHtml(drawer.dataset.upsellBundleLabel)}</span>
          <div class="cart-upsell-option-heading">
            <strong>${escapeHtml(offer.bundleTitle)}</strong>
            <span class="cart-upsell-save">${escapeHtml(replaceTemplate(drawer.dataset.upsellSaveTemplate, { '__AMOUNT__': money(offer.bundleSavings) }))}</span>
          </div>
          <p>${escapeHtml(drawer.dataset.upsellBundleBody)}</p>
          <div class="cart-upsell-price"><strong>${money(offer.bundlePrice)}</strong><s>${money(offer.bundleRegular)}</s></div>
          <a class="cart-upsell-cta" href="${escapeHtml(offer.bundleUrl)}">${escapeHtml(replaceTemplate(drawer.dataset.upsellViewBundleTemplate, { '__BUNDLE__': offer.bundleTitle, '__AMOUNT__': money(offer.bundleSavings) }))}</a>
        </article>
      </div>
    </aside>`;
  };

  const renderSubscriptionChoice = (item) => {
    if (item.selling_plan_allocation || bundleIdFor(item) || item.quantity < 1 || item.quantity > 3) return '';
    const offer = upsellProducts[item.handle];
    if (!offer || Number(offer.variantId) !== Number(item.variant_id)) return '';
    const plan = offer.subscriptionPlans?.[item.quantity - 1];
    if (!plan || plan.price * item.quantity >= item.final_line_price) return '';
    const label = replaceTemplate(drawer.dataset.subscribeLabel, { '__PRICE__': money(plan.price * item.quantity) });
    return `<div class="cart-subscription-choice"><button type="button" class="cart-upsell-cta" data-cart-subscribe="${escapeHtml(item.key)}" data-subscribe-plan="${plan.id}" data-subscribe-quantity="${item.quantity}">${escapeHtml(label)}</button><p>${escapeHtml(plan.name)} · ${escapeHtml(drawer.dataset.subscribeFlexible)}</p></div>`;
  };

  const cartEntries = (cart) => {
    const entries = [];
    const bundles = new Map();

    cart.items.forEach((item, index) => {
      const bundleId = bundleIdFor(item);
      if (!bundleId) {
        entries.push({ type: 'item', item, index });
        return;
      }

      if (!bundles.has(bundleId)) {
        const entry = { type: 'bundle', bundleId, items: [] };
        bundles.set(bundleId, entry);
        entries.push(entry);
      }
      bundles.get(bundleId).items.push({ item, index });
    });

    return entries;
  };

  const renderCart = (cart) => {
    const entries = cartEntries(cart);
    const displayCount = entries.reduce((count, entry) => {
      if (entry.type === 'bundle') return count + 1;
      return count + entry.item.quantity;
    }, 0);

    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = displayCount; });

    if (!cart.item_count) {
      content.innerHTML = `<div class="cart-drawer-empty"><strong>Your bag is empty.</strong><span>Find a little something for your routine.</span><a class="btn cart-drawer-view" href="${root}collections/all">Shop all</a></div>`;
      return;
    }

    const items = entries.map((entry) => {
      if (entry.type === 'bundle') {
        const first = entry.items[0]?.item;
        const title = first?.properties?._bundleName || 'Wellness set';
        const url = first?.properties?._bundleProductUrl || `${root}collections/wellness-bundles`;
        const image = first?.image
          ? `<img src="${escapeHtml(resizedImage(first.image))}" alt="${escapeHtml(title)}" width="180" height="180" loading="lazy">`
          : '';
        const productNames = entry.items.map(({ item }) => item.product_title).join(' + ');
        const sellingPlan = entry.items.find(({ item }) => item.selling_plan_allocation)
          ?.item.selling_plan_allocation?.selling_plan?.name;
        const meta = [productNames, sellingPlan].filter(Boolean).map(escapeHtml).join(' · ');
        const total = entry.items.reduce((sum, { item }) => sum + item.final_line_price, 0);

        return `<article class="cart-drawer-item cart-drawer-item--bundle">
          <a class="cart-drawer-item-image" href="${escapeHtml(url)}">${image}</a>
          <div>
            <h3><a href="${escapeHtml(url)}">${escapeHtml(title)}</a></h3>
            ${meta ? `<p class="cart-drawer-item-meta">${meta}</p>` : ''}
            <button type="button" class="cart-drawer-remove-bundle" data-cart-remove-bundle="${escapeHtml(entry.bundleId)}">Remove set</button>
          </div>
          <strong class="cart-drawer-item-price">${money(total)}</strong>
        </article>`;
      }

      const { item, index } = entry;
      const image = item.image ? `<img src="${escapeHtml(resizedImage(item.image))}" alt="${escapeHtml(item.product_title)}" width="180" height="180" loading="lazy">` : '';
      const meta = itemMeta(item);
      return `<article class="cart-drawer-item">
        <a class="cart-drawer-item-image" href="${escapeHtml(item.url)}">${image}</a>
        <div>
          <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>
          ${meta ? `<p class="cart-drawer-item-meta">${meta}</p>` : ''}
          ${renderSubscriptionChoice(item)}
          <div class="cart-drawer-quantity" aria-label="Quantity for ${escapeHtml(item.product_title)}">
            <button type="button" data-cart-change="${index + 1}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-change="${index + 1}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <strong class="cart-drawer-item-price">${money(item.final_line_price)}</strong>
      </article>`;
    }).join('');

    const remaining = Math.max(0, freeShippingThreshold - cart.total_price);
    const progress = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);
    const progressText = remaining > 0
      ? `<span>You're <strong>${money(remaining)}</strong> away from free shipping.</span>`
      : '<strong>You unlocked free shipping ✦</strong>';

    const upsell = renderUpsell(cart, entries);

    content.innerHTML = `<div class="cart-drawer-items">${items}</div>
      ${upsell}
      <div class="cart-drawer-footer">
        <div class="cart-drawer-progress"><p>${progressText}</p><div class="cart-drawer-progress-track"><div class="cart-drawer-progress-bar" style="width:${progress}%"></div></div></div>
        <div class="cart-drawer-subtotal"><span>Subtotal</span><strong>${money(cart.total_price)}</strong></div>
        <form action="${root}cart" method="post"><button class="btn cart-drawer-checkout" type="submit" name="checkout">Checkout · ${money(cart.total_price)}</button></form>
        <a class="cart-drawer-view" href="${root}cart">View full bag</a>
      </div>`;
  };

  const showError = (message = 'Something went wrong. Please try again.') => {
    content.insertAdjacentHTML('afterbegin', `<p class="cart-drawer-error" role="alert">${escapeHtml(message)}</p>`);
  };

  const getCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your bag could not be loaded.');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const openDrawer = async (trigger = document.activeElement) => {
    returnFocus = trigger;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    try { await getCart(); } catch (error) { showError(error.message); }
    panel.focus();
  };

  const closeDrawer = () => {
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (returnFocus?.focus) returnFocus.focus();
  };

  window.alsOpenCartDrawer = openDrawer;
  window.alsRefreshCartDrawer = getCart;

  // Sync the badge immediately so page loads and drawer opens use the same
  // bundle-aware count. The drawer is also ready before the first click.
  getCart().catch(() => {});

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-cart-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      closeDrawer();
      return;
    }

    const subscribe = event.target.closest('[data-cart-subscribe]');
    if (subscribe) {
      subscribe.disabled = true;
      const cartPage = subscribe.closest('[data-cart-page]');
      try {
        const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!currentResponse.ok) throw new Error(drawer.dataset.subscribeError);
        const current = await currentResponse.json();
        const lineIndex = current.items.findIndex((item) => item.key === subscribe.dataset.cartSubscribe);
        const item = current.items[lineIndex];
        if (!item || item.quantity !== Number(subscribe.dataset.subscribeQuantity) || item.selling_plan_allocation || bundleIdFor(item)) throw new Error(drawer.dataset.subscribeChanged);
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: lineIndex + 1, quantity: item.quantity, selling_plan: Number(subscribe.dataset.subscribePlan) })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || drawer.dataset.subscribeError);
        if (cartPage) window.location.reload();
        else renderCart(result);
      } catch (error) {
        subscribe.disabled = false;
        if (cartPage) {
          const alert = cartPage.querySelector('[data-cart-page-error]');
          alert.textContent = error.message;
          alert.hidden = false;
        } else showError(error.message);
      }
      return;
    }

    const removeBundle = event.target.closest('[data-cart-remove-bundle]');
    if (removeBundle) {
      removeBundle.disabled = true;
      try {
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
        const updates = {};
        cart.items.forEach((item) => {
          if (bundleIdFor(item) === removeBundle.dataset.cartRemoveBundle) updates[item.key] = 0;
        });
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error('This set could not be removed.');
        renderCart(await response.json());
      } catch (error) {
        removeBundle.disabled = false;
        showError(error.message);
      }
      return;
    }

    const change = event.target.closest('[data-cart-change]');
    if (!change) return;
    change.disabled = true;
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(change.dataset.cartChange), quantity: Number(change.dataset.cartQuantity) })
      });
      if (!response.ok) throw new Error('That quantity could not be updated.');
      renderCart(await response.json());
    } catch (error) {
      change.disabled = false;
      showError(error.message);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
  });

  document.addEventListener('submit', async (event) => {
    const routineForm = event.target.closest('form[data-subscription-bundle]');
    if (routineForm) {
      event.preventDefault();
      const submit = routineForm.querySelector('[type="submit"]');
      const routineName = routineForm.dataset.routineName || 'Monthly routine';
      const items = [...routineForm.querySelectorAll('[data-subscription-item]')].map((item) => ({
        id: item.dataset.variantId,
        quantity: 1,
        selling_plan: item.dataset.sellingPlanId,
        properties: { _als_recurring_routine: routineName }
      }));

      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }

      try {
        if (!items.length) throw new Error('This routine is not available right now.');
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This routine could not be added.');
        await openDrawer(submit || routineForm);
      } catch (error) {
        await openDrawer(submit || routineForm);
        showError(error.message);
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
      }
      return;
    }

    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.querySelector('.rbr')) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const subscriptionWidget = form.querySelector('[data-subscription-widget]');
    const selectedPurchase = subscriptionWidget?.querySelector('[data-purchase-option]:checked');
    const sellingPlanInput = subscriptionWidget?.querySelector('[data-selling-plan-input]');

    if (selectedPurchase?.value === 'subscription' && sellingPlanInput) {
      const quantity = Number(form.querySelector('[data-pack-option]:checked')?.value || 1);
      const sellingPlanId = subscriptionWidget.getAttribute(`data-selling-plan-${quantity}`)
        || selectedPurchase.dataset.sellingPlanId;
      sellingPlanInput.value = sellingPlanId || '';
      sellingPlanInput.disabled = !sellingPlanId;
    }

    if (submit) submit.disabled = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This item could not be added.');
      await openDrawer(submit || form);
    } catch (error) {
      await openDrawer(submit || form);
      showError(error.message);
    } finally {
      if (submit) submit.disabled = false;
    }
  });
})();


